import React, { useEffect, useRef, useState } from "react";
import { FiCamera } from "react-icons/fi";
import { toast } from "react-toastify";
import UserAvatar, { notifyUserPhotoUpdated } from "../desktop/UserAvatar";
import { api } from "../../utils/api";
import "./ProfilePhotoEditor.css";

const getUpdatePayload = (data, companyId) => {
  const fields = [
    "employee_code", "first_name", "last_name", "email_id", "country_code",
    "mobile_no", "password", "role", "desktop_access", "is_allow_mobilepunch",
    "department", "designation", "location", "reporting_manager", "gender", "isactive", "theme",
  ];
  const payload = { company_id: data.company_id ?? companyId };
  fields.forEach((field) => {
    if (data[field] !== undefined) payload[field] = data[field];
  });

  let devices = data.device_allowed ?? [];
  if (typeof devices === "string") {
    try {
      devices = JSON.parse(devices);
    } catch {
      devices = devices.split(",").filter((id) => id.trim());
    }
  }
  if (devices === null || devices === "") devices = [];
  payload.device_allowed = (Array.isArray(devices) ? devices : [devices])
    .map((device) => Number(typeof device === "object" ? device?.id : device));
  if (payload.device_allowed.some((id) => !Number.isInteger(id) || id <= 0)) {
    throw new Error("Unable to read existing device access. Please try again.");
  }
  return payload;
};

export default function ProfilePhotoEditor({ userId, companyId, name }) {
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const saveLock = useRef(false);
  const mounted = useRef(true);
  const saveController = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      saveController.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!imageFile) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const choosePhoto = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || saveLock.current) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.warning("Please select a JPG, PNG or WebP image");
      return;
    }
    setImageFile(file);
  };

  const savePhoto = async () => {
    if (!imageFile || !userId || !companyId || saveLock.current) return;
    saveLock.current = true;
    setSaving(true);
    const controller = new AbortController();
    saveController.current = controller;
    try {
      // The update endpoint needs the full user record, even for a photo change.
      const response = await api.get(`/user/${userId}/${companyId}`, {
        signal: controller.signal,
        cache: "no-store",
      });
      const result = await response.json();
      if (!response.ok || result?.status === false) {
        throw new Error(result?.message || "Unable to load your profile");
      }
      if (controller.signal.aborted) return;

      const formData = new FormData();
      formData.append("request", JSON.stringify(getUpdatePayload(result.data ?? result, companyId)));
      formData.append("file", imageFile);
      const updateResponse = await api.put(`/user/update/${userId}`, formData, {
        signal: controller.signal,
      });
      const updateResult = await updateResponse.json();
      if (!updateResponse.ok || updateResult?.status === false) {
        throw new Error(updateResult?.message || "Unable to update profile photo");
      }
      if (!mounted.current || controller.signal.aborted) return;
      setImageFile(null);
      notifyUserPhotoUpdated(userId, companyId);
      toast.success("Profile photo updated successfully");
    } catch (error) {
      if (mounted.current && !controller.signal.aborted) {
        toast.error(error.message || "Unable to update profile photo");
      }
    } finally {
      saveLock.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <div className="mobile-profile-photo" aria-busy={saving}>
      <button
        type="button"
        className="mobile-profile-photo-picker"
        aria-label="Change profile photo"
        onClick={() => inputRef.current?.click()}
        disabled={saving || !userId || !companyId}
      >
        <UserAvatar userId={userId} companyId={companyId} name={name} preview={preview} large />
        <span className="mobile-profile-photo-camera"><FiCamera aria-hidden="true" /></span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="mobile-profile-photo-input"
        aria-label="Choose profile photo"
        onChange={choosePhoto}
        disabled={saving}
      />
      {imageFile && (
        <div className="mobile-profile-photo-actions">
          <button type="button" className="mobile-profile-photo-save" onClick={savePhoto} disabled={saving}>
            {saving ? "Saving..." : "Save photo"}
          </button>
          <button type="button" className="mobile-profile-photo-cancel" onClick={() => setImageFile(null)} disabled={saving}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
